import { apiSlice } from './apiSlice'
import type { SaleReferencePageResponse } from './saleContractApi'

// ===================== saleControlApi =====================
/**
 * RTK Query endpoints cho module Sales Control (page `frmCtrlSalesMain`).
 *
 * - Filter dùng chung header: `startDate`, `endDate`, `isFinalized` — bám
 *   `form_frmCtrlSalesMain.txt` (`start_date`, `end_date`, `txtIsForced`).
 * - Mỗi tab có 1 endpoint list riêng, URL `/sale-controls/<resource>`.
 * - Backend endpoint chưa được implement; các hook sẽ trả về list rỗng cho đến
 *   khi `backend/app/api/v1/endpoints/sale_controls.py` được thêm.
 */

export const SALE_CONTROL_EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

export interface SaleControlListResponse {
  items: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

export type SaleControlFinalizedFilter = 'all' | 'yes' | 'no'

export interface SaleControlListQuery {
  page?: number
  pageSize?: number
  q?: string
  /** ISO `YYYY-MM-DD`, optional — apply on `delivery_from` (shipment period). */
  startDate?: string
  endDate?: string
  /** ComboBox `txtIsForced` — `all` ⇒ omit. */
  isFinalized?: SaleControlFinalizedFilter
}

const trimQuery = (args: SaleControlListQuery): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  if (typeof args.page === 'number') out.page = args.page
  if (typeof args.pageSize === 'number') out.pageSize = args.pageSize
  if (args.q?.trim()) out.q = args.q.trim()
  if (args.startDate?.trim()) out.startDate = args.startDate.trim()
  if (args.endDate?.trim()) out.endDate = args.endDate.trim()
  if (args.isFinalized && args.isFinalized !== 'all') out.isFinalized = args.isFinalized
  return out
}

export const saleControlApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** Tab 1 — `public_mv_local_s` (Access query `CtrlSalesQryLocalS`). */
    getCtrlSalesLocalS: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/local-s', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 2 — Sales Allocation (`CtrlSalesQrySalesAlc`). Stub. */
    getCtrlSalesSaleAlc: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/sale-alc', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 3 — Delivery Order (`CtrlSalesQryDO`). Stub. */
    getCtrlSalesDeliveryOrder: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/delivery-order', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 4 — Invoice (`CtrlSalesQryInvoice`). Stub. */
    getCtrlSalesInvoice: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/invoice', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 5 — Release Order (`CtrlSalesQryRO`). Stub. */
    getCtrlSalesReleaseOrder: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/release-order', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 6 — Claim (`CtrlSalesQryClaim`). Stub. */
    getCtrlSalesClaim: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/claim', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 7 — Credit Note (`CtrlSalesQryCredit`). Stub. */
    getCtrlSalesCredit: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/credit-note', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 8 — Debit Note (`CtrlSalesQryDebit`). Stub. */
    getCtrlSalesDebit: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/debit-note', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 9 — Internal Moving (`CtrlSalesQryMoving`). Stub. */
    getCtrlSalesMoving: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/moving', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnForcedClose` (form `frmCtrlSalesQryLocalS_CloseAdd`). */
    forcedCloseLocalS: builder.mutation<{ message: string }, { contractSale: string; finalizedDate: string; note?: string }>({
      query: (body) => ({ url: '/sale-controls/local-s/forced-close', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnUnforced` (admin only). */
    reopenLocalS: builder.mutation<{ message: string }, { contractSale: string }>({
      query: (body) => ({ url: '/sale-controls/local-s/reopen', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnFillingDocs` (toggle `is_ctrl_filldoc`). */
    toggleFillingDocsLocalS: builder.mutation<{ message: string }, { contractSale: string; isCtrlFilldoc: boolean }>({
      query: (body) => ({ url: '/sale-controls/local-s/filling-docs', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useGetCtrlSalesLocalSQuery,
  useGetCtrlSalesSaleAlcQuery,
  useGetCtrlSalesDeliveryOrderQuery,
  useGetCtrlSalesInvoiceQuery,
  useGetCtrlSalesReleaseOrderQuery,
  useGetCtrlSalesClaimQuery,
  useGetCtrlSalesCreditQuery,
  useGetCtrlSalesDebitQuery,
  useGetCtrlSalesMovingQuery,
  useForcedCloseLocalSMutation,
  useReopenLocalSMutation,
  useToggleFillingDocsLocalSMutation,
} = saleControlApi


// ===================== saleControlReferenceApi =====================
/**
 * Sales Control › References switchboard API — fork của getSaleReferenceRows (Sales
 * Contract) sang scope Sales Control. Endpoint BE: GET /sale-control-references/{page_key}.
 */

export type { SaleReferencePageResponse } from './saleContractApi'

export interface SaleControlReferenceRowsQuery {
  pageKey: string
  limit?: number
  /** Date/Shipment Period filter (VirtualWH.date_in; Annex → local_s shipment period). */
  startDate?: string
  endDate?: string
  /** Is Finalized filter (all|yes|no) — hiện áp cho Annex (theo local_s.is_finalized). */
  isFinalized?: string
}

export const saleControlReferenceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleControlReferenceRows: builder.query<SaleReferencePageResponse, SaleControlReferenceRowsQuery>({
      query: ({ pageKey, limit = 1000000, startDate, endDate, isFinalized }) => ({
        url: `/references/${encodeURIComponent(pageKey)}`,
        params: {
          limit,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(isFinalized && isFinalized !== 'all' ? { is_finalized: isFinalized } : {}),
        },
      }),
      providesTags: (_r, _e, { pageKey }) => [
        { type: 'SaleControlReference' as const, id: pageKey },
        { type: 'SaleControlReference' as const, id: 'LIST' },
      ],
    }),
  }),
})

export const { useGetSaleControlReferenceRowsQuery } = saleControlReferenceApi

// ===================== saleControlAllocationApi =====================
/**
 * Sale Allocation New/Edit/Copy — Access `frmCtrlSalesQrySaleAlcAdd`
 * (table `public.sales_allocation_scs`, PK `sales_allocation_scs_id`).
 *
 * NOTE — BACKEND NOT YET BUILT. The Sales Control list reads
 * `public.mv_sales_allocation_scs` (api_erp_prod.py), but there is no CRUD nor
 * combo-lookup endpoint for the Add form. Every endpoint declared here is a
 * placeholder mirroring the proven `saleDeliveryOrderApi` shape so the dialog can
 * compile and wire today; the URLs marked `TODO BE` must be implemented before
 * this dialog is enabled in production. Lookup queries fall back to `{ items: [] }`
 * (empty combos) until then — no silent fake data.
 *
 * Cache: list query providesTags `SaleControl` (saleControlApi). Mutations here
 * invalidate `SaleControl` so the Sales Allocation datasheet refetches (Access
 * `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`).
 */

/** GET /{id}/form shape — raw columns of one row for Edit/Copy hydrate. */
export interface SaleAllocationForm {
  sales_allocation_scs_id: number
  /** Stored as a single `sale_contract` column; main form splits it into the two
   *  combos by prefix (S* → sale contract, NPE* → consignment). */
  sale_contract: string | null
  delivery_order: string | null
  client: string | null
  contract_no_of_bag: number | null
  contract_quantity: number | null
  m_date: string | null
  m_truck_no: string | null
  m_net_weight: number | null
  from_warehouse: string | null
  to_warehouse: string | null
  container_no: string | null
  seal_no: string | null
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
  max_gross_weight: number | null
  /** Stored receipt_no + gdn_or_rdn discriminator; GDN → Receipt No field,
   *  RDN → Virtual Warehouse field (main form logic). */
  receipt_no: string | null
  gdn_or_rdn: string | null
  export_no_of_bag: number | null
  export_quantity: number | null
  net_weight_at_buyer: number | null
  deduction_at_buyer_kg: number | null
  deduction_at_buyer_vnd: number | null
  allocating_bag: number | null
  allocating_quantity: number | null
  booking_no: string | null
  /** Calculated (Access =DLookup delivery_terms by sale_contract) — display-only. */
  contract_terms: string | null
  note: string | null
}

/** New/Edit/Copy write payload — snake_case → sales_allocation_scs columns.
 *  `sale_contract` carries either the S-contract or the consignment contract
 *  (the dialog resolves which, exactly as Access btnSave_Click does). */
export interface SaleAllocationWrite {
  sales_allocation_scs_id?: number | null
  sale_contract?: string | null
  delivery_order?: string | null
  m_date?: string | null
  from_warehouse?: string | null
  to_warehouse?: string | null
  m_truck_no?: string | null
  m_net_weight?: number | null
  container_no?: string | null
  seal_no?: string | null
  number_bags?: number | null
  tare_weight?: number | null
  tare_carton_pallet?: number | null
  tare_cont?: number | null
  max_gross_weight?: number | null
  receipt_no?: string | null
  gdn_or_rdn?: string | null
  net_weight_at_buyer?: number | null
  deduction_at_buyer_kg?: number | null
  deduction_at_buyer_vnd?: number | null
  allocating_bag?: number | null
  allocating_quantity?: number | null
  note?: string | null
}

export interface SaleAlcLookupItem {
  value: string
  label: string
  /** delivery_terms of the contract (S-contract lookup only) — feeds the
   *  readonly Delivery Terms field (Access `contract_terms` DLookup). */
  terms?: string | null
  /** Extra info per option — vd Receipt No (GDN) mang `{ truck }` để dropdown
   *  Edit-Similar-DO hiện Truck No kèm mã. */
  meta?: { truck?: string | null } | null
}
export interface SaleAlcLookupResponse { items: SaleAlcLookupItem[] }

/** delivery_order_AfterUpdate snapshot — DLookup net_weight_kg / from_warehouse /
 *  place_of_delivery from delivery_order_scs by do_no. */
export interface SaleAlcDoSnapshot {
  contract_quantity: number | null
  from_warehouse: string | null
  to_warehouse: string | null
  booking_no: string | null
}

/** receipt_no / virtual_wh AfterUpdate snapshot — DSum bags + quantity, plus the
 *  contract terms (CIF/FOB/FOT) auto-fill of allocating_bag / allocating_quantity. */
export interface SaleAlcReceiptSnapshot {
  export_no_of_bag: number | null
  export_quantity: number | null
  /** Set only when the sale contract delivery_terms is CIF/FOB/FOT (else null). */
  allocating_bag: number | null
  allocating_quantity: number | null
}

export const saleAllocationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── frmCtrlSalesQrySaleAlcAdd ─────────────────────────────────────────
    // TODO BE: GET /sale-controls/sale-alc/{id}/form — raw row for Edit/Copy.
    getSaleAllocationForm: builder.query<SaleAllocationForm, number>({
      query: (id) => `/sale-controls/sale-alc/${id}/form`,
    }),
    // btnDelete_Click — soft-delete (is_status = 1). DELETE /sale-controls/sale-alc/{id}.
    deleteSaleAllocation: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-controls/sale-alc/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: POST /sale-controls/sale-alc — INSERT (btnSave_Click INSERT branch).
    createSaleAllocation: builder.mutation<{ sales_allocation_scs_id: number }, SaleAllocationWrite>({
      query: (body) => ({ url: '/sale-controls/sale-alc', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: PUT /sale-controls/sale-alc/{id} — UPDATE (btnSave_Click UPDATE branch).
    updateSaleAllocation: builder.mutation<{ sales_allocation_scs_id: number }, { id: number; body: SaleAllocationWrite }>({
      query: ({ id, body }) => ({ url: `/sale-controls/sale-alc/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: GET /sale-controls/sale-alc/check/seal-no — seal_no uniqueness
    // (seal_no_AfterUpdate + btnSave errCheck 4: DCount over mv_sales_allocation_scs,
    // excluding the current id on Edit).
    checkSaleAllocationSeal: builder.query<{ exists: boolean }, { sealNo: string; excludeId?: number }>({
      query: ({ sealNo, excludeId }) => ({
        url: '/sale-controls/sale-alc/check/seal-no',
        params: { sealNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: GET /sale-controls/sale-alc/snapshot/do — delivery_order_AfterUpdate.
    getSaleAllocationDoSnapshot: builder.query<SaleAlcDoSnapshot, string>({
      query: (doNo) => ({ url: '/sale-controls/sale-alc/snapshot/do', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: GET /sale-controls/sale-alc/snapshot/receipt — receipt_no/virtual_wh
    // AfterUpdate (kind = 'gdn' | 'rdn'; saleContract for the CIF/FOB/FOT auto-fill).
    getSaleAllocationReceiptSnapshot: builder.query<
      SaleAlcReceiptSnapshot,
      { kind: 'gdn' | 'rdn'; value: string; saleContract?: string }
    >({
      query: ({ kind, value, saleContract }) => ({
        url: '/sale-controls/sale-alc/snapshot/receipt',
        params: { kind, value, ...(saleContract ? { saleContract } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),

    // ── Combo lookups (Access RowSource queries) ──────────────────────────
    // TODO BE: each returns { items: [{ value, label }] }. Until built they 404 →
    // the dialog guards with `data?.items ?? []` (empty combo, no fake data).

    // sale_contract: SELECT contract_sale, cp_code, name FROM public_mv_local_s.
    saleAlcLookupSaleContracts: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/sale-contracts', params: q ? { q } : {} }),
    }),
    // consignment_contract: SELECT contract_no, cp_code, name FROM public_v_consignment_s.
    saleAlcLookupConsignments: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/consignments', params: q ? { q } : {} }),
    }),
    // delivery_order: SELECT do_no FROM public_delivery_order_scs WHERE is_status=0.
    // `contract` (enhancement ngoài Access): lọc DO theo sale_contract khi form đã
    // chọn S Contract / Consignment.
    saleAlcLookupDeliveryOrders: builder.query<SaleAlcLookupResponse, { q?: string; contract?: string } | void>({
      query: (arg) => ({
        url: '/sale-controls/sale-alc/lookups/delivery-orders',
        params: { ...(arg?.q ? { q: arg.q } : {}), ...(arg?.contract ? { contract: arg.contract } : {}) },
      }),
    }),
    // from_warehouse / to_warehouse: SELECT IIF(...) AS warehouse_1 FROM ...
    saleAlcLookupWarehouses: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/warehouses', params: q ? { q } : {} }),
    }),
    // receipt_no: SELECT DISTINCT gdn_id FROM public_v_factory_delivery.
    // `doNo` (enhancement ngoài Access): lọc GDN theo DO đã chọn ở form.
    saleAlcLookupReceiptNos: builder.query<SaleAlcLookupResponse, { q?: string; doNo?: string } | void>({
      query: (arg) => ({
        url: '/sale-controls/sale-alc/lookups/receipt-nos',
        params: { ...(arg?.q ? { q: arg.q } : {}), ...(arg?.doNo ? { doNo: arg.doNo } : {}) },
      }),
    }),
    // virtual_wh: SELECT DISTINCT receipt_no FROM public_virtual_warehouse WHERE is_status=0.
    // `doNo` (enhancement ngoài Access): lọc Virtual WH theo DO đã chọn ở form.
    saleAlcLookupVirtualWarehouses: builder.query<SaleAlcLookupResponse, { q?: string; doNo?: string } | void>({
      query: (arg) => ({
        url: '/sale-controls/sale-alc/lookups/virtual-warehouses',
        params: { ...(arg?.q ? { q: arg.q } : {}), ...(arg?.doNo ? { doNo: arg.doNo } : {}) },
      }),
    }),
  }),
})

export const {
  useLazyGetSaleAllocationFormQuery,
  useDeleteSaleAllocationMutation,
  useCreateSaleAllocationMutation,
  useUpdateSaleAllocationMutation,
  useLazyCheckSaleAllocationSealQuery,
  useLazyGetSaleAllocationDoSnapshotQuery,
  useLazyGetSaleAllocationReceiptSnapshotQuery,
  useSaleAlcLookupSaleContractsQuery,
  useSaleAlcLookupConsignmentsQuery,
  useSaleAlcLookupDeliveryOrdersQuery,
  useSaleAlcLookupWarehousesQuery,
  useSaleAlcLookupReceiptNosQuery,
  useSaleAlcLookupVirtualWarehousesQuery,
} = saleAllocationApi


// ===================== saleControlAllocationBbgnPrintApi =====================
/**
 * Sale Allocation — "Print - BBGN" report (Access `rpCtrlSalesBBGN`,
 * "Biên bản giao nhận" / goods handover record).
 *
 * Access port (VBA `btnPrintBBGN_Click` on `frmCtrlSalesMainSubFormSaleAlc`):
 *   1. Require the selected row's `delivery_order` (empty → warn
 *      "Delivery order must be not null!").
 *   2. Set query `CtrlSalesQryBBGNPrint` =
 *        SELECT (SELECT COUNT(*) FROM public_v_sale_allocation_print_scs AS sub
 *                WHERE sub.row_num <= main.row_num
 *                  AND sub.delivery_order = '<do>') AS row_number, main.*
 *        FROM public_v_sale_allocation_print_scs AS main
 *        WHERE main.delivery_order = '<do>' AND sale_contract = '<sc>';
 *   3. Open report `rpCtrlSalesBBGN` (a single-record handover document).
 *
 * Backend is live: GET /sale-allocations/bbgn-print/preview (registered in
 * `api.py`, service `ctrl_sales_bbgn_print.py`). The lazy query returns the
 * rendered HTML built from `public.v_sale_allocation_print_scs`.
 *
 * Cache tag reused: `SaleControl` (shared with the Sales Allocation datasheet).
 */

/** Request for the BBGN print preview — one delivery_order + its sale_contract. */
export interface BbgnPrintPreviewRequest {
  /** Selected row `delivery_order` (Access WHERE main.delivery_order = :do). */
  deliveryOrder: string
  /** Selected row `sale_contract` (Access WHERE sale_contract = :sc). */
  saleContract: string
}

export interface BbgnPrintPreviewResponse {
  /** Fully-built HTML (`access-report` + `.ctl` absolute layout) for the iframe. */
  html: string
  /** Suggested dialog title id part (the delivery_order). */
  title: string
  /** Optional info/warning relayed to the user (English). */
  message?: string
}

export const saleAllocationBbgnPrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Build the BBGN (goods handover record) report HTML for one delivery order.
     * Access: `btnPrintBBGN_Click` sets `CtrlSalesQryBBGNPrint` + opens rpCtrlSalesBBGN.
     * GET /sale-allocations/bbgn-print/preview?delivery_order=<do>&sale_contract=<sc>
     *   → { html, title, message? }. BE renders the `rpCtrlSalesBBGN` absolute layout
     *   from `public_v_sale_allocation_print_scs` (filtered by delivery_order +
     *   sale_contract) plus the static TLC seller block from
     *   `public_tmp_add_information` (type='tlc_name_vn' / 'tlc_address_law_vn').
     */
    getBbgnPrintPreview: builder.query<BbgnPrintPreviewResponse, BbgnPrintPreviewRequest>({
      query: ({ deliveryOrder, saleContract }) => ({
        url: '/sale-allocations/bbgn-print/preview',
        params: { delivery_order: deliveryOrder, sale_contract: saleContract },
      }),
    }),
  }),
  overrideExisting: false,
})

export const { useLazyGetBbgnPrintPreviewQuery } = saleAllocationBbgnPrintApi


// ===================== saleControlAllocationEditBookingApi =====================
/**
 * Sale Allocation › Edit (Booking) — Access `frmCtrlSalesQrySaleAlc_EditBK_Add`
 * (subform `frmCtrlSalesQrySaleAlc_EditBK`, table `public.sales_allocation_scs`,
 * PK `sales_allocation_scs_id`).
 *
 * This is the toolbar action "Edit (Booking)" on the Sales Allocation tab. The
 * Access main-form opens with NO selected row: the user picks a Booking No in the
 * header combo, which loads every allocation row carrying that booking_no into a
 * grid (Access copies `public_v_sales_allocation_scs` → temp table and requeries
 * the subform). The user edits per-row container/seal/bags/tare fields, then Save
 * runs one UPDATE per row over `public_sales_allocation_scs` (only the 6 editable
 * columns + audit; `WHERE sales_allocation_scs_id = … AND is_status = 0`).
 *
 * BACKEND (live): endpoints served by `allocbulk_router` (prefix
 * `/sale-controls/sale-alc`) in `endpoints/sale_controls.py` → repo
 * `sale_control_pg.py` (`lookup_booking_nos` / `get_edit_booking_rows` /
 * `update_edit_booking_rows`). Booking lookup + row load read
 * `public.v_delivery_order_scs` / `public.v_sales_allocation_scs`; batch save
 * writes base `public.sales_allocation_scs` (only the 6 editable columns + audit,
 * `WHERE sales_allocation_scs_id = ? AND is_status = 0`), 1:1 with Access
 * `btnSave_Click`.
 *
 * Cache: mutation invalidates `SaleControl` so the Sales Allocation datasheet
 * refetches (Access `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`).
 */

/** One allocation row loaded for a booking_no (subform `RecordSource`). */
export interface SaleAlcEditBookingRow {
  sales_allocation_scs_id: number
  /* ── Read-only context columns (subform display, no "(Edit)" label) ── */
  sale_contract: string | null
  delivery_order: string | null
  client: string | null
  contract_quality: string | null
  m_date: string | null
  from_warehouse: string | null
  to_warehouse: string | null
  /* ── Editable columns (subform "(Edit)" labels; btnSave UPDATE list) ── */
  container_no: string | null
  seal_no: string | null
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
}

export interface SaleAlcEditBookingRowsResponse { items: SaleAlcEditBookingRow[] }

/** Booking No combo option (Access RowSource: SELECT DISTINCT booking_no, do_no
 *  FROM public_v_delivery_order_scs WHERE booking_no is not null …). */
export interface SaleAlcEditBookingLookupItem { value: string; label: string }
export interface SaleAlcEditBookingLookupResponse { items: SaleAlcEditBookingLookupItem[] }

/** One row's editable payload — only the 6 columns Access UPDATEs per row. */
export interface SaleAlcEditBookingPatch {
  sales_allocation_scs_id: number
  container_no?: string | null
  seal_no?: string | null
  number_bags?: number | null
  tare_weight?: number | null
  tare_carton_pallet?: number | null
  tare_cont?: number | null
}

/** Batch UPDATE payload — booking_no scope + one patch per allocation row. */
export interface SaleAlcEditBookingWrite {
  booking_no: string
  rows: SaleAlcEditBookingPatch[]
}

export const saleAllocationEditBookingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── frmCtrlSalesQrySaleAlc_EditBK_Add ─────────────────────────────────
    // GET /sale-controls/sale-alc/edit-booking/lookups/booking-nos —
    // SELECT DISTINCT booking_no FROM public_v_delivery_order_scs
    // WHERE booking_no is not null and Len(Trim(booking_no)) > 0.
    saleAlcEditBookingLookupBookingNos: builder.query<SaleAlcEditBookingLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/edit-booking/lookups/booking-nos',
        params: q ? { q } : {},
      }),
    }),
    // GET /sale-controls/sale-alc/edit-booking/rows — allocation rows for a
    // booking_no (booking_no_AfterUpdate: SELECT * FROM public_v_sales_allocation_scs
    // WHERE booking_no = ? ORDER BY sales_allocation_scs_id DESC).
    getSaleAlcEditBookingRows: builder.query<SaleAlcEditBookingRowsResponse, string>({
      query: (bookingNo) => ({
        url: '/sale-controls/sale-alc/edit-booking/rows',
        params: { bookingNo },
      }),
      keepUnusedDataFor: 0,
    }),
    // PUT /sale-controls/sale-alc/edit-booking — batch UPDATE
    // (btnSave_Click: one UPDATE per row over public_sales_allocation_scs, setting
    // container_no/seal_no/number_bags/tare_weight/tare_carton_pallet/tare_cont +
    // updated_date/updated_by, WHERE sales_allocation_scs_id = ? AND is_status = 0).
    updateSaleAlcEditBooking: builder.mutation<{ updated: number }, SaleAlcEditBookingWrite>({
      query: (body) => ({ url: '/sale-controls/sale-alc/edit-booking', method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useSaleAlcEditBookingLookupBookingNosQuery,
  useLazyGetSaleAlcEditBookingRowsQuery,
  useUpdateSaleAlcEditBookingMutation,
} = saleAllocationEditBookingApi


// ===================== saleControlAllocationEditSameDoApi =====================
/**
 * saleAllocationEditSameDoApi — RTK Query slice for the Sale Allocation
 * "Edit (similar DO)" bulk-edit grid (Access `frmCtrlSalesQrySaleAlc_Edit_SameDO`
 * hosting subform `frmCtrlSalesQrySaleAlc_TickCont`).
 *
 * Access flow (btnEditMany_Click): clears tmp `public_tmp_sales_alc_tick`, then
 * `INSERT … SELECT *, 1 AS checked_row FROM public_v_sales_allocation_scs
 *  WHERE delivery_order = '<do_no>'` and opens the grid. btnSave_Click loops the
 * ticked rows and `UPDATE public_sales_allocation_scs SET receipt_no, allocating_bag,
 * allocating_quantity, updated_*` for each.
 *
 * → On the web there is no tmp table: we fetch the rows for one DO, edit the three
 *   editable columns inline (receipt_no / allocating_bag / allocating_quantity) per
 *   ticked row, and PUT the ticked rows back in one batch.
 *
 * BE NOT BUILT YET — endpoints below are declared so the dialog can wire its hooks;
 * until the backend ships they 404 and the grid guards with `data?.rows ?? []`
 * (no fabricated rows). See TODO markers per endpoint.
 *
 * Reuses the shared `SaleControl` cache tag so the bulk update invalidates the
 * Sale Allocation list (Access `…!subForm.Requery`).
 */

/** One row of `public_v_sales_allocation_scs` for a given delivery_order (camelCase). */
export interface SaleAllocationSameDoRow {
  /** sales_allocation_scs_id — PK targeted by the UPDATE. */
  salesAllocationScsId: number
  /** receipt_no — editable. */
  receiptNo: string | null
  /** allocating_bag — editable (Format Standard). */
  allocatingBag: number | null
  /** allocating_quantity — editable (Format Standard). */
  allocatingQuantity: number | null
  // ── Display-only context columns (TickCont datasheet order by Top) ──
  containerNo: string | null
  mDate: string | null
  mTruckNo: string | null
  mNetWeight: number | null
  saleContract: string | null
  deliveryOrder: string | null
  bookingNo: string | null
  contractQuality: string | null
  fromWarehouse: string | null
}

export interface SaleAllocationSameDoResponse {
  rows: SaleAllocationSameDoRow[]
}

/** One ticked row's editable payload for the batch UPDATE. */
export interface SaleAllocationSameDoUpdateItem {
  salesAllocationScsId: number
  receiptNo: string | null
  allocatingBag: number | null
  allocatingQuantity: number | null
}

export interface SaleAllocationSameDoUpdateRequest {
  rows: SaleAllocationSameDoUpdateItem[]
}

export const saleAllocationEditSameDoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // TODO BE: GET /sale-controls/sale-alc/same-do?doNo=<delivery_order>
    //   SELECT … FROM public_v_sales_allocation_scs WHERE delivery_order = :doNo.
    //   (Access btnEditMany INSERT … SELECT into tmp_sales_alc_tick.)
    getSaleAllocationsSameDo: builder.query<SaleAllocationSameDoResponse, string>({
      query: (doNo) => ({ url: '/sale-controls/sale-alc/same-do', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: PUT /sale-controls/sale-alc/same-do — batch UPDATE the ticked rows.
    //   For each row: UPDATE public_sales_allocation_scs SET receipt_no,
    //   allocating_bag, allocating_quantity, updated_date, updated_by
    //   WHERE sales_allocation_scs_id = :id AND is_status = 0.
    updateSaleAllocationsSameDo: builder.mutation<{ updated: number }, SaleAllocationSameDoUpdateRequest>({
      query: (body) => ({ url: '/sale-controls/sale-alc/same-do', method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useGetSaleAllocationsSameDoQuery,
  useUpdateSaleAllocationsSameDoMutation,
} = saleAllocationEditSameDoApi


// ===================== saleControlAllocationMassUploadApi =====================
/**
 * saleAllocationMassUploadApi — RTK Query endpoints for the Sales Control ›
 * Sales Allocation "Mass Upload" dialog (Access `frmCtrlSalesQrySaleAlcAddMany`,
 * opened from `btnInsert`/"Mass Upload"). Mass-insert MANY rows into
 * `public_sales_allocation_scs` in one shot.
 *
 * Injected into the shared `apiSlice` so it reuses the same base query + the
 * existing `'SaleControl'` tag — bulk insert invalidates the Sales Allocation
 * list (it providesTags `'SaleControl'`), mirroring Access
 * `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`.
 *
 * The Access header Client combo (`cp_vn`) is `Visible = NotDefault` (hidden) so
 * it is not rendered; the client lookup + request field are therefore pruned.
 * Contract No / DO No / warehouse lookups are typing aids only — the grid is a
 * free-text paste target (Access temp table `public_tmp_addmany_salesalc`); no
 * typed/pasted value is dropped. Existence is validated at Save by the backend.
 */

export interface SaleAlcMassLookupItem {
  value: string
  label: string
}
export interface SaleAlcMassLookupResponse {
  items: SaleAlcMassLookupItem[]
}

/**
 * One row of the mass-insert payload. Keys map 1:1 to the INSERT column list in
 * Access `btnSave_Click` (temp table `public_tmp_addmany_salesalc` → target
 * `public_sales_allocation_scs`). Strings empty/optional → BE coerces to NULL.
 */
export interface SaleAlcMassRow {
  sale_contract: string
  delivery_order: string
  m_date: string | null
  m_truck_no: string
  from_warehouse: string
  to_warehouse: string
  m_net_weight: number | null
  container_no: string
  seal_no: string
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
  max_gross_weight: number | null
  allocating_bag: number | null
  allocating_quantity: number | null
}

/** Per-row validation error returned by the dry-run / bulk endpoints. */
export interface SaleAlcMassRowError {
  field?: keyof SaleAlcMassRow
  message: string
}
export interface SaleAlcMassRowVerdict {
  ok: boolean
  errors: SaleAlcMassRowError[]
}
/** Dry-run cross-check result (one verdict per submitted row, same order). */
export interface SaleAlcMassValidateResponse {
  rows: SaleAlcMassRowVerdict[]
}
/** Bulk insert result. */
export interface SaleAlcMassCreateResponse {
  created: number
}

interface SaleAlcMassRequest {
  rows: SaleAlcMassRow[]
}

export const saleAllocationMassUploadApi = apiSlice.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // ── Grid typing-aid lookups (free-text cells; suggestions only) ─────────
    // Contract No suggestions from public_mv_local_s (contract_sale). The grid is
    // free-text — typed/pasted values that are not in this list are kept and
    // validated at Save (BE), never dropped.
    // GET /sale-controls/sale-alc/mass/contracts → { items: [{ value, label }] }
    saleAlcMassContracts: builder.query<SaleAlcMassLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/sale-controls/sale-alc/mass/contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    // DO No suggestions from public_v_delivery_order_scs (do_no, is_status=0).
    // GET /sale-controls/sale-alc/mass/delivery-orders → { items: [{ value, label }] }
    saleAlcMassDeliveryOrders: builder.query<SaleAlcMassLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/mass/delivery-orders',
        params: q ? { q } : {},
      }),
    }),
    // from_warehouse / to_warehouse suggestions. Access derives these from the DO
    // (Form_BeforeRender / btnSave); the cell is a free-text override only.
    // GET /sale-controls/sale-alc/mass/warehouses → { items: [{ value, label }] }
    saleAlcMassWarehouses: builder.query<SaleAlcMassLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/mass/warehouses',
        params: q ? { q } : {},
      }),
    }),

    // ── Dry-run cross-check (Access btnSave per-row validation loop) ─────────
    // Mirrors the VBA error scan: Contract No exists, DO No exists, Seal No not
    // already used in public_mv_sales_allocation_scs. Returns one verdict per row.
    // POST /sale-controls/sale-alc/mass/validate { rows } → { rows: [...] }
    validateSaleAlcMass: builder.mutation<SaleAlcMassValidateResponse, SaleAlcMassRequest>({
      query: (body) => ({
        url: '/sale-controls/sale-alc/mass/validate',
        method: 'POST',
        body,
      }),
    }),

    // ── Bulk insert (Access btnSave INSERT loop, one transaction) ───────────
    // BE re-validates (race-safe), derives from/to warehouse from the DO when a
    // cell is blank, then inserts all rows; on success the parent list refetches
    // via the shared 'SaleControl' tag (= Access subForm.Requery).
    // POST /sale-controls/sale-alc/mass { rows } → { created }
    bulkCreateSaleAlcMass: builder.mutation<SaleAlcMassCreateResponse, SaleAlcMassRequest>({
      query: (body) => ({
        url: '/sale-controls/sale-alc/mass',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useSaleAlcMassContractsQuery,
  useSaleAlcMassDeliveryOrdersQuery,
  useSaleAlcMassWarehousesQuery,
  useValidateSaleAlcMassMutation,
  useBulkCreateSaleAlcMassMutation,
} = saleAllocationMassUploadApi


// ===================== saleControlAllocationVgmPrintApi =====================
/**
 * Sale Allocation — "Print - VGM" chooser + report (Access `frmCtrlSalesVGMPr_Choose`
 * + `rpCtrlSalesVGM_NN` / `rpCtrlSalesVGMTotal`).
 *
 * Access port (VBA `btnPrintVGM_Click` on `frmCtrlSalesMainSubFormSaleAlc`):
 *   1. DELETE FROM public_tmp_sales_alc_tick
 *   2. INSERT INTO public_tmp_sales_alc_tick
 *        SELECT *, 1 AS checked_row FROM public_v_sales_allocation_scs
 *        WHERE delivery_order = '<do>' AND IIf(IsNull(container_no),'',container_no) <> ''
 *   3. Open chooser `frmCtrlSalesVGMPr_Choose` bound to that DO.
 * Chooser lets the user tick the containers to print + pick a container size, then
 * `btnPrint_Click` builds `CtrlSalesQryVGMPrint` over the ticked container_no list and
 * opens `rpCtrlSalesVGM_<count>` (1..15 rows) or `rpCtrlSalesVGMTotal` for larger sets.
 *
 * NOTE — BACKEND NOT YET BUILT. Every endpoint below is a placeholder mirroring the
 * proven `saleDeliveryOrderApi` shape so the chooser dialog can compile and wire today.
 * The URLs marked `TODO BE` MUST be implemented before this dialog is enabled in
 * production. Queries fall back to empty payloads (no silent fake data) until then.
 *
 * Cache tag reused: `SaleControl` (shared with the Sales Allocation datasheet).
 */

/** One selectable container row for the VGM chooser tick list.
 *  Sourced from `public_v_sales_allocation_scs` for the given delivery_order
 *  (only rows with a non-empty container_no). */
export interface VgmContainerRow {
  /** Stable key — the allocation row PK. */
  salesAllocationScsId: number
  containerNo: string
  /** Max gross weight (kg) — report column C. */
  maxGrossWeight: number | null
  /** Verified gross mass (kg) — report column D. */
  vgm: number | null
}

export interface VgmContainersResponse {
  deliveryOrder: string
  items: VgmContainerRow[]
}

/** Payload for the VGM print preview — built from the ticked containers + size. */
export interface VgmPrintPreviewRequest {
  /** Selected delivery_order (chooser is bound to one DO). */
  doNo: string
  /** Ticked container_no list (report rows, max 15 for `rpCtrlSalesVGM_NN`). */
  containerNos: string[]
  /** `container_type` combo — Access Value List `"20 '";"40 '";"other"`. */
  containerType: string
}

export interface VgmPrintPreviewResponse {
  /** Fully-built HTML (`access-report` + `.ctl` absolute layout) for the iframe. */
  html: string
  /** Suggested dialog title id part (the delivery_order). */
  title: string
  /** Optional info/warning relayed to the user (English). */
  message?: string
}

export const saleAllocationVgmPrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Containers eligible for the VGM chooser tick list.
     * Access: DELETE/INSERT into public_tmp_sales_alc_tick then bind the subform.
     * TODO BE — GET /sale-allocations/vgm-print/containers?do_no=<do>
     *   → { deliveryOrder, items: VgmContainerRow[] } from public_v_sales_allocation_scs
     *     WHERE delivery_order = :do AND container_no <> ''.
     */
    getVgmPrintContainers: builder.query<VgmContainersResponse, string>({
      query: (doNo) => ({
        url: '/sale-allocations/vgm-print/containers',
        params: { do_no: doNo },
      }),
      providesTags: ['SaleControl'],
    }),

    /**
     * Build the VGM report HTML for the ticked containers.
     * Access: `btnPrint_Click` builds `CtrlSalesQryVGMPrint` + opens rpCtrlSalesVGM_NN.
     * TODO BE — POST /sale-allocations/vgm-print/preview
     *   body VgmPrintPreviewRequest → { html, title, message? }.
     *   BE picks the row count → renders the matching `rpCtrlSalesVGM_NN` template.
     */
    getVgmPrintPreview: builder.query<VgmPrintPreviewResponse, VgmPrintPreviewRequest>({
      query: (body) => ({
        url: '/sale-allocations/vgm-print/preview',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetVgmPrintContainersQuery,
  useLazyGetVgmPrintPreviewQuery,
} = saleAllocationVgmPrintApi


// ===================== saleControlClaimApi =====================
/** Mirrors FastAPI `SaleClaimOut` (camelCase aliases) — frmCtrlSalesQryClaim datasheet. */
export interface SaleClaim {
  id: number
  claimScsCode: string | null
  claimId: string | null
  dateClaim: string | null
  customerInvoiceNo: string | null
  forDo: string | null
  contractRef: string | null
  client: string | null
  claimType: string | null
  claimCondition: string | null
  customerInvAtd: string | null
  dischargeDate: string | null
  weighingDate: string | null
  validWeighingDate: string | null
  validClaimDate: string | null
  acceptOrReject: string | null
  finalStatus: string | null
  paymentWeightTerms: string | null
  price: number | null
  grossShippedWeight: number | null
  netShippedWeight: number | null
  receiptWh: string | null
  grossLandedWeight: number | null
  netLandedWeight: number | null
  difference: number | null
  franchise: number | null
  claimWeight: number | null
  claimWeightValue: number | null
  claimReason: string | null
  claimQualityValue: number | null
  claimOtherValue: number | null
  creditNo: string | null
  fullCreditNo: string | null
  claimId2: string | null
  creditDate: string | null
  creditAmount: number | null
  customerDebitNo: string | null
  fromWarehouse: string | null
  debitDate: string | null
  debitNo: string | null
  fullDebitNo: string | null
  claimId3: string | null
  debitorTo: string | null
  debitCpCode: string | null
  debitName: string | null
  debitorRefNo: string | null
  debitReason: string | null
  debitValue: number | null
  note: string | null
  dateUpdated: string | null
  personUpdated: string | null
  address: string | null
  name: string | null
  paymentTerms: string | null
  warehouse: string | null
  blNumber: string | null
  destinationPort: string | null
  portOfLoading: string | null
  placeOfDelivery: string | null
  vesselNameVoyage: string | null
  goodsDescription: string | null
  deliveryTerms: string | null
  atd: string | null
  buyersRef: string | null
  customerInvPrice: string | null
  customerInvNw: number | null
  customerInvGw: number | null
  claimNote: string | null
  priceUnit: string | null
  roe: number | null
  claimValueFinal: number | null
  deliveryFrom: string | null
  isFinalized: number | null
  destinationPortName: string | null
  portOfLoadingName: string | null
}

export interface SaleClaimListResponse {
  items: SaleClaim[]
  total: number
  page: number
  pageSize: number
}

export interface SaleClaimListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryClaimAdd New/Edit payload — camelCase → claim_scs. */
export interface SaleClaimWrite {
  claimScsCode?: string | null
  customerInvoiceNo?: string | null
  dateClaim?: string | null
  claimType?: string | null
  claimCondition?: string | null
  dischargeDate?: string | null
  weighingDate?: string | null
  finalStatus?: string | null
  priceUnit?: string | null
  claimReason?: string | null
  claimQualityValue?: number | null
  claimOtherValue?: number | null
  roe?: number | null
  note?: string | null
}

export type SaleClaimForm = Record<string, unknown> & { id: number }

export interface ClaimConditionSnapshot {
  weighingDate: number | null
  weightClaim: number | null
  qualityClaims: number | null
  others: number | null
}

export interface ClaimLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface ClaimLookupResponse { items: ClaimLookupItem[]; total: number }
export interface SuggestClaimNoResponse { index: number; claimScsCode: string; exists: boolean }

export const saleClaimApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleClaims: builder.query<SaleClaimListResponse, SaleClaimListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-claims',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleClaim'],
    }),

    getSaleClaim: builder.query<SaleClaim, number>({
      query: (id) => `/sale-claims/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleClaim', id }],
    }),

    deleteSaleClaim: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-claims/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleClaim'],
    }),

    getSaleClaimCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-claims/access/can-delete',
    }),

    // ── frmCtrlSalesQryClaimAdd ───────────────────────────────────────────
    getSaleClaimForm: builder.query<SaleClaimForm, number>({
      query: (id) => `/sale-claims/${id}/form`,
    }),
    createSaleClaim: builder.mutation<{ claim_scs_id: number }, SaleClaimWrite>({
      query: (body) => ({ url: '/sale-claims', method: 'POST', body }),
      invalidatesTags: ['SaleClaim'],
    }),
    updateSaleClaim: builder.mutation<{ claim_scs_id: number }, { id: number; body: SaleClaimWrite }>({
      query: ({ id, body }) => ({ url: `/sale-claims/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleClaim'],
    }),
    suggestClaimNo: builder.query<SuggestClaimNoResponse, void>({
      query: () => '/sale-claims/suggest/claim-no',
      keepUnusedDataFor: 0,
    }),
    checkClaimNo: builder.query<SuggestClaimNoResponse, { claimScsCode: string; excludeId?: number }>({
      query: ({ claimScsCode, excludeId }) => ({
        url: '/sale-claims/check/claim-no',
        params: { claimScsCode, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getClaimConditionSnapshot: builder.query<ClaimConditionSnapshot, string>({
      query: (organization) => ({ url: '/sale-claims/condition-snapshot', params: { organization } }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    claimLookupCustomerInvoices: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/customer-invoices', params: q ? { q } : {} }),
    }),
    claimLookupTypes: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/types', params: q ? { q } : {} }),
    }),
    claimLookupConditions: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/conditions', params: q ? { q } : {} }),
    }),
    claimLookupPriceUnits: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/price-units', params: q ? { q } : {} }),
    }),
    // Print — chooser pre-fill (giá trị claimable_at/notion đã lưu, Access DLookUp).
    getClaimPrintInputs: builder.query<
      { claimableAt: string | null; notion: string | null },
      number
    >({
      query: (claimId) => ({ url: `/sale-claims/${claimId}/print/inputs` }),
    }),
    // Print — chooser (claimable_at/notion) → rpCtrlSalesClaimCheck* (Weight vs Others/Quality).
    getClaimPrintPreview: builder.mutation<
      { title: string; html: string; message?: string },
      { claimId: number; claimableAt?: string; notion?: string }
    >({
      query: ({ claimId, claimableAt, notion }) => ({
        url: `/sale-claims/${claimId}/print/preview`,
        method: 'POST',
        body: { claimableAt, notion },
      }),
    }),
  }),
})

export const {
  useGetSaleClaimsQuery,
  useGetSaleClaimQuery,
  useDeleteSaleClaimMutation,
  useGetSaleClaimCanHardDeleteQuery,
  useLazyGetSaleClaimFormQuery,
  useCreateSaleClaimMutation,
  useUpdateSaleClaimMutation,
  useLazySuggestClaimNoQuery,
  useLazyCheckClaimNoQuery,
  useLazyGetClaimConditionSnapshotQuery,
  useClaimLookupCustomerInvoicesQuery,
  useClaimLookupTypesQuery,
  useClaimLookupConditionsQuery,
  useClaimLookupPriceUnitsQuery,
  useLazyGetClaimPrintInputsQuery,
  useGetClaimPrintPreviewMutation,
} = saleClaimApi


// ===================== saleControlCreditNoteApi =====================
/** Mirrors FastAPI `SaleCreditNoteOut` (camelCase) — frmCtrlSalesQryCredit datasheet. */
export interface SaleCreditNote {
  id: number
  creditNo: string | null
  claimScsCode: string | null
  customerDebitNo: string | null
  creditDate: string | null
  creditAmount: number | null
  doNo: string | null
  loadingPlace: string | null
  dateUpdated: string | null
  personUpdated: string | null
  claimType: string | null
  note: string | null
  currency: string | null
  paymentDate: string | null
  fx: number | null
  priceUnit: string | null
  unit: string | null
  client: string | null
}

export interface SaleCreditNoteListResponse {
  items: SaleCreditNote[]
  total: number
  page: number
  pageSize: number
}

export interface SaleCreditNoteListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export interface SuggestIndexResponse {
  index: number
  exists: boolean
}

export interface CnLookupItem { value: string; label: string }
export interface CnLookupResponse { items: CnLookupItem[]; total: number }

/** GET /{id}/form shape for Edit hydrate. */
export type SaleCreditNoteForm = Record<string, unknown> & { id: number }

/** claim_scs_code_AfterUpdate DLookUps over v_claim_scs (readonly display). */
export interface CreditClaimSnapshot {
  client: string | null
  claimType: string | null
  claimValue: number | null
  claimCurrency: string | null
  invoiceNo: string | null
}

/** Create/Update payload — frmCtrlSalesQryCreditAdd → claim_credit_scs. */
export interface SaleCreditNoteWrite {
  claimScsCode?: string | null
  creditNo?: string | null
  customerDebitNo?: string | null
  creditDate?: string | null
  creditAmount?: number | null
  note?: string | null
  priceUnit?: string | null
  paymentDate?: string | null
  fx?: number | null
}

export const saleCreditNoteApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleCreditNotes: builder.query<SaleCreditNoteListResponse, SaleCreditNoteListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-credit-notes',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleCreditNote'],
    }),

    getSaleCreditNote: builder.query<SaleCreditNote, number>({
      query: (id) => `/sale-credit-notes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleCreditNote', id }],
    }),

    deleteSaleCreditNote: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-credit-notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleCreditNote'],
    }),

    getSaleCreditNoteCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-credit-notes/access/can-delete',
    }),

    // ── frmCtrlSalesQryCreditAdd ─────────────────────────────────────────────
    getSaleCreditNoteForm: builder.query<SaleCreditNoteForm, number>({
      query: (id) => `/sale-credit-notes/${id}/form`,
    }),
    createSaleCreditNote: builder.mutation<{ claim_credit_scs_id: number }, SaleCreditNoteWrite>({
      query: (body) => ({ url: '/sale-credit-notes', method: 'POST', body }),
      invalidatesTags: ['SaleCreditNote'],
    }),
    updateSaleCreditNote: builder.mutation<{ claim_credit_scs_id: number }, { id: number; body: SaleCreditNoteWrite }>({
      query: ({ id, body }) => ({ url: `/sale-credit-notes/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleCreditNote'],
    }),
    suggestCreditNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-credit-notes/suggest/credit-no',
    }),
    checkCreditNo: builder.query<SuggestIndexResponse, { creditNo: string; excludeId?: number }>({
      query: ({ creditNo, excludeId }) => ({
        url: '/sale-credit-notes/check/credit-no',
        params: { creditNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getCreditClaimSnapshot: builder.query<CreditClaimSnapshot, string>({
      query: (claimScsCode) => ({ url: '/sale-credit-notes/claim-snapshot', params: { claimScsCode } }),
      keepUnusedDataFor: 0,
    }),
    cnLookupClaimCodes: builder.query<CnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-credit-notes/lookups/claim-codes', params: q ? { q } : {} }),
    }),
    cnLookupPriceUnits: builder.query<CnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-credit-notes/lookups/price-units', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleCreditNotesQuery,
  useGetSaleCreditNoteQuery,
  useDeleteSaleCreditNoteMutation,
  useGetSaleCreditNoteCanHardDeleteQuery,
  useLazyGetSaleCreditNoteFormQuery,
  useCreateSaleCreditNoteMutation,
  useUpdateSaleCreditNoteMutation,
  useLazySuggestCreditNoQuery,
  useLazyCheckCreditNoQuery,
  useLazyGetCreditClaimSnapshotQuery,
  useCnLookupClaimCodesQuery,
  useCnLookupPriceUnitsQuery,
} = saleCreditNoteApi


// ===================== saleControlCreditNotePrintApi =====================
/**
 * Sales Control › Credit Note PRINT preview query (Access Credit Note screen).
 * New slice — conflict-free with saleCreditNoteApi.ts (owned by another agent).
 *
 *   getCreditNotePrintPreview → GET /sale-credit-notes/print/preview?id={id}
 *
 * The print is keyed by ONE claim_credit_scs_id (the selected row). The backend
 * resolves the layout variant (Weight / Quality / Others) from the row
 * claim_type. Returns { title, html, message }. 422 detail = user-facing English
 * message (not found / claim type not accurate). keepUnusedDataFor: 0 (always
 * refetch).
 */
type PrintPreview = { title: string; html: string; message?: string }

export const saleCreditNotePrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCreditNotePrintPreview: builder.query<PrintPreview, number>({
      query: (id) => ({
        url: '/sale-credit-notes/print/preview',
        params: { id },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
  overrideExisting: false,
})

export const { useLazyGetCreditNotePrintPreviewQuery } = saleCreditNotePrintApi


// ===================== saleControlDebitNoteApi =====================
/** Mirrors FastAPI `SaleDebitNoteOut` (camelCase) — frmCtrlSalesQryDebit datasheet. */
export interface SaleDebitNote {
  id: number
  debitNo: string | null
  claimScsCode: string | null
  debitDate: string | null
  debitorToName: string | null
  debitCpCode: string | null
  debitorRefNo: string | null
  debitCpName: string | null
  debitReason: string | null
  debitValue: number | null
  note: string | null
  dateUpdate: string | null
  personUpdate: string | null
  claimType: string | null
  priceUnit: string | null
  fx: number | null
  unit: string | null
  currency: string | null
}

export interface SaleDebitNoteListResponse {
  items: SaleDebitNote[]
  total: number
  page: number
  pageSize: number
}

export interface SaleDebitNoteListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}


export interface DnLookupItem { value: string; label: string; meta?: { debitorTo?: string; name?: string } | null }
export interface DnLookupResponse { items: DnLookupItem[]; total: number }

/** GET /{id}/form shape for Edit hydrate. */
export type SaleDebitNoteForm = Record<string, unknown> & { id: number }

/** claim_scs_code_AfterUpdate DLookUps over v_claim_scs (readonly display). */
export interface DebitClaimSnapshot {
  claimType: string | null
  claimValue: number | null
  claimCurrency: string | null
  invoiceNo: string | null
}

/** Create/Update payload — frmCtrlSalesQryDebitAdd → claim_debit_scs. */
export interface SaleDebitNoteWrite {
  claimScsCode?: string | null
  debitNo?: string | null
  debitDate?: string | null
  debitorTo?: string | null
  debitCpCode?: string | null
  debitorRefNo?: string | null
  debitReason?: string | null
  debitValue?: number | null
  note?: string | null
  priceUnit?: string | null
  fx?: number | null
}

export const saleDebitNoteApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleDebitNotes: builder.query<SaleDebitNoteListResponse, SaleDebitNoteListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-debit-notes',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleDebitNote'],
    }),

    getSaleDebitNote: builder.query<SaleDebitNote, number>({
      query: (id) => `/sale-debit-notes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleDebitNote', id }],
    }),

    deleteSaleDebitNote: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-debit-notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleDebitNote'],
    }),

    getSaleDebitNoteCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-debit-notes/access/can-delete',
    }),

    // ── frmCtrlSalesQryDebitAdd ──────────────────────────────────────────────
    getSaleDebitNoteForm: builder.query<SaleDebitNoteForm, number>({
      query: (id) => `/sale-debit-notes/${id}/form`,
    }),
    createSaleDebitNote: builder.mutation<{ claim_debit_scs_id: number }, SaleDebitNoteWrite>({
      query: (body) => ({ url: '/sale-debit-notes', method: 'POST', body }),
      invalidatesTags: ['SaleDebitNote'],
    }),
    updateSaleDebitNote: builder.mutation<{ claim_debit_scs_id: number }, { id: number; body: SaleDebitNoteWrite }>({
      query: ({ id, body }) => ({ url: `/sale-debit-notes/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleDebitNote'],
    }),
    suggestDebitNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-debit-notes/suggest/debit-no',
    }),
    checkDebitNo: builder.query<SuggestIndexResponse, { debitNo: string; excludeId?: number }>({
      query: ({ debitNo, excludeId }) => ({
        url: '/sale-debit-notes/check/debit-no',
        params: { debitNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getDebitClaimSnapshot: builder.query<DebitClaimSnapshot, string>({
      query: (claimScsCode) => ({ url: '/sale-debit-notes/claim-snapshot', params: { claimScsCode } }),
      keepUnusedDataFor: 0,
    }),
    dnLookupClaimCodes: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/claim-codes', params: q ? { q } : {} }),
    }),
    dnLookupPriceUnits: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/price-units', params: q ? { q } : {} }),
    }),
    dnLookupCpList: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/cp-list', params: q ? { q } : {} }),
    }),
    dnLookupServices: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/services', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleDebitNotesQuery,
  useGetSaleDebitNoteQuery,
  useDeleteSaleDebitNoteMutation,
  useGetSaleDebitNoteCanHardDeleteQuery,
  useLazyGetSaleDebitNoteFormQuery,
  useCreateSaleDebitNoteMutation,
  useUpdateSaleDebitNoteMutation,
  useLazySuggestDebitNoQuery,
  useLazyCheckDebitNoQuery,
  useLazyGetDebitClaimSnapshotQuery,
  useDnLookupClaimCodesQuery,
  useDnLookupPriceUnitsQuery,
  useDnLookupCpListQuery,
  useDnLookupServicesQuery,
} = saleDebitNoteApi


// ===================== saleControlDebitNotePrintApi =====================
/**
 * Sales Control › Debit Note PRINT preview query (Access Debit Note screen).
 * New slice — conflict-free with saleDebitNoteApi.ts (owned by another agent).
 *
 *   getDebitNotePrintPreview → GET /sale-debit-notes/print/preview?id={id}
 *
 * The print is keyed by ONE claim_debit_scs_id (the selected row). The backend
 * resolves the layout variant from the row claim_type ("Weight claims" → Weight
 * layout; any other type → Quality layout). Returns { title, html, message }.
 * 422 detail = user-facing English message (debit note not found). Frame passes
 * only the id. keepUnusedDataFor: 0 (always refetch).
 */

export const saleDebitNotePrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDebitNotePrintPreview: builder.query<PrintPreview, number>({
      query: (id) => ({
        url: '/sale-debit-notes/print/preview',
        params: { id },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
  overrideExisting: false,
})

export const { useLazyGetDebitNotePrintPreviewQuery } = saleDebitNotePrintApi


// ===================== saleControlDeliveryOrderApi =====================
/** Mirrors FastAPI `SaleDeliveryOrderOut` (camelCase aliases) — frmCtrlSalesQryDO datasheet. */
export interface SaleDeliveryOrder {
  id: number
  fromWarehouse: string | null
  saleContract: string | null
  buyersRef: string | null
  doNo: string | null
  doDate: string | null
  loadingDate: string | null
  accumulateDelivered: number | null
  pending: number | null
  nwKgs: number | null
  buyerName: string | null
  contractWeightKg: number | null
  quality: string | null
  certification: string | null
  deliveryTerms: string | null
  contractWarehouse: string | null
  shippingMonth: string | null
  shippingTo: string | null
  packaging: string | null
  icoNo: string | null
  marking: string | null
  dunnage: string | null
  typeOfInspection: string | null
  inspectionName: string | null
  fumiDose: string | null
  fumigationName: string | null
  noContTruckNum: number | null
  truckName: string | null
  customs: string | null
  customsName: string | null
  bookingNote: string | null
  bookingNo: string | null
  freightPayable: string | null
  blNumber: string | null
  shippingLinesName: string | null
  vesselNameVoyage: string | null
  exportTerminalName: string | null
  internalTransshipmentPort: string | null
  portOfLoadingCode: string | null
  portOfLoadingName: string | null
  transhipmentPortCode: string | null
  transhipmentPortName: string | null
  destinationPortCode: string | null
  destinationPortName: string | null
  placeOfDelivery: string | null
  cutOff: string | null
  etd: string | null
  eta: string | null
  atd: string | null
  docsPresentationDate: string | null
  sendingDocsTo: string | null
  indication: string | null
  docsReceiverFullname: string | null
  fullName: string | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface SaleDeliveryOrderListResponse {
  items: SaleDeliveryOrder[]
  total: number
  page: number
  pageSize: number
}

export interface SaleDeliveryOrderListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryDOAdd New/Edit/Copy payload — camelCase → delivery_order_scs. */
export interface SaleDeliveryOrderWrite {
  doNo?: string | null
  doDate?: string | null
  warehouseType?: string | null
  fromWarehouse?: string | null
  saleContract?: string | null
  loadingDate?: string | null
  netWeightKg?: number | null
  icoNo?: string | null
  marking?: string | null
  dunnage?: string | null
  typeOfInspection?: string | null
  inspectionCompany?: string | null
  fumiDose?: string | null
  fumigationCompany?: string | null
  noContTruck?: number | null
  truckCompany?: string | null
  bookingNote?: string | null
  bookingNo?: string | null
  freightPayable?: string | null
  blNumber?: string | null
  shippingLine?: string | null
  vesselName?: string | null
  voyage?: string | null
  exportTerminal?: string | null
  portOfLoading?: string | null
  transhipmentPort?: string | null
  destinationPort?: string | null
  placeOfDelivery?: string | null
  cutOff?: string | null
  etd?: string | null
  eta?: string | null
  atd?: string | null
  docsPresentationDate?: string | null
  documentReceiver?: string | null
  pic?: string | null
  customs?: string | null
  internalTransshipmentPort?: string | null
  /** Nguồn match khi DO tạo từ Matching P-S "Save vs DO". */
  matchType?: string | null
  matchId?: number | null
}

/** GET /{id}/form shape (codes + display labels) for Edit/Copy hydrate. */
export type SaleDeliveryOrderForm = Record<string, unknown> & { id: number }

/** frmCtrlSalesDispatchPrint subform row (cb<i> + quantity1<i> + quantity2<i>). */
export interface DispatchDocRow {
  docName: string | null
  numOriginal: number | null
  numCopies: number | null
}
export interface DispatchPrintForm {
  cpCode: string | null
  rows: DispatchDocRow[]
  docOptions: string[]
}

export interface DoLookupItem { value: string; label: string }
export interface DoLookupResponse { items: DoLookupItem[]; total: number }
export interface DoSnapshotResponse { contractTotal: number; deliveredTotal: number; todeDelivery: number }

export const saleDeliveryOrderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleDeliveryOrders: builder.query<SaleDeliveryOrderListResponse, SaleDeliveryOrderListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-delivery-orders',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleDeliveryOrder'],
    }),

    getSaleDeliveryOrder: builder.query<SaleDeliveryOrder, number>({
      query: (id) => `/sale-delivery-orders/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleDeliveryOrder', id }],
    }),

    deleteSaleDeliveryOrder: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-delivery-orders/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),

    /** Email-permissioned hard-delete access (legacy btnDelete rule). */
    getSaleDeliveryOrderCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-delivery-orders/access/can-delete',
    }),

    // ── frmCtrlSalesQryDOAdd ──────────────────────────────────────────────
    getSaleDeliveryOrderForm: builder.query<SaleDeliveryOrderForm, number>({
      query: (id) => `/sale-delivery-orders/${id}/form`,
    }),
    createSaleDeliveryOrder: builder.mutation<{ delivery_order_scs_id: number }, SaleDeliveryOrderWrite>({
      query: (body) => ({ url: '/sale-delivery-orders', method: 'POST', body }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),
    updateSaleDeliveryOrder: builder.mutation<{ delivery_order_scs_id: number }, { id: number; body: SaleDeliveryOrderWrite }>({
      query: ({ id, body }) => ({ url: `/sale-delivery-orders/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),
    suggestDoNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-delivery-orders/suggest/do-no',
    }),
    suggestIcoNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-delivery-orders/suggest/ico-no',
    }),
    checkDoNo: builder.query<SuggestIndexResponse, { index: number; excludeId?: number }>({
      query: ({ index, excludeId }) => ({
        url: '/sale-delivery-orders/check/do-no',
        params: { index, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getDoSnapshot: builder.query<DoSnapshotResponse, { saleContract: string; excludeId?: number }>({
      query: ({ saleContract, excludeId }) => ({
        url: '/sale-delivery-orders/snapshot',
        params: { saleContract, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups (combo sources)
    doLookupSaleContracts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/sale-contracts', params: q ? { q } : {} }),
    }),
    doLookupWarehouses: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/warehouses', params: q ? { q } : {} }),
    }),
    doLookupDunnage: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/dunnage', params: q ? { q } : {} }),
    }),
    doLookupInspectionTypes: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/inspection-types', params: q ? { q } : {} }),
    }),
    doLookupServices: builder.query<DoLookupResponse, { types: string; q?: string }>({
      query: ({ types, q }) => ({ url: '/sale-delivery-orders/lookups/services', params: { types, ...(q ? { q } : {}) } }),
    }),
    doLookupDocsDispatch: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/docs-dispatch', params: q ? { q } : {} }),
    }),
    doLookupPorts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/ports', params: q ? { q } : {} }),
    }),
    doLookupVessels: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/vessels', params: q ? { q } : {} }),
    }),
    doLookupExportTerminals: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/export-terminals', params: q ? { q } : {} }),
    }),
    doLookupInternalPorts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/internal-ports', params: q ? { q } : {} }),
    }),
    doLookupPics: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/pics', params: q ? { q } : {} }),
    }),
    // Print — SA (BL No): Access rpCtrlSalesSA master-detail report → web HTML.
    // BE renders one Shipping Advice grouped by sale_contract + b_l_number.
    // 422 detail = user-facing English message (no rows / missing keys).
    getSaPrintPreview: builder.query<
      { title: string; html: string; message?: string },
      { saleContract: string; blNumber: string }
    >({
      query: ({ saleContract, blNumber }) => ({
        url: '/sale-delivery-orders/sa-print/preview',
        params: { saleContract, blNumber },
      }),
      keepUnusedDataFor: 0,
    }),
    // Print — DO: render rpCtrlSalesDO for one delivery order (do_no).
    getDoPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/do-print/preview', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // Print — Dispatch: render rpCtrlSalesDispatch cover letter for one do_no.
    getDispatchPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/dispatch-print/preview', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // Print — Dispatch (editable): frmCtrlSalesDispatchPrint subform — load + persist
    // the shipping-document list, then render. btnPrint = save → render.
    getDispatchPrintForm: builder.query<DispatchPrintForm, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/dispatch-print/form', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    saveDispatchPrint: builder.mutation<
      { title: string; html: string; message?: string },
      { doNo: string; rows: DispatchDocRow[] }
    >({
      query: (body) => ({ url: '/sale-delivery-orders/dispatch-print/save', method: 'POST', body }),
    }),
  }),
})

export const {
  useGetSaleDeliveryOrdersQuery,
  useGetSaleDeliveryOrderQuery,
  useDeleteSaleDeliveryOrderMutation,
  useGetSaleDeliveryOrderCanHardDeleteQuery,
  useLazyGetSaleDeliveryOrderFormQuery,
  useCreateSaleDeliveryOrderMutation,
  useUpdateSaleDeliveryOrderMutation,
  useLazySuggestDoNoQuery,
  useLazySuggestIcoNoQuery,
  useLazyCheckDoNoQuery,
  useLazyGetDoSnapshotQuery,
  useDoLookupSaleContractsQuery,
  useDoLookupWarehousesQuery,
  useDoLookupDunnageQuery,
  useDoLookupInspectionTypesQuery,
  useDoLookupServicesQuery,
  useDoLookupDocsDispatchQuery,
  useDoLookupPortsQuery,
  useDoLookupVesselsQuery,
  useDoLookupExportTerminalsQuery,
  useDoLookupInternalPortsQuery,
  useDoLookupPicsQuery,
  useLazyGetSaPrintPreviewQuery,
  useLazyGetDoPrintPreviewQuery,
  useLazyGetDispatchPrintPreviewQuery,
  useLazyGetDispatchPrintFormQuery,
  useSaveDispatchPrintMutation,
} = saleDeliveryOrderApi


// ===================== saleControlInvoiceApi =====================
/** Mirrors FastAPI `SaleInvoiceOut` (camelCase aliases) — frmCtrlSalesQryInvoice datasheet. */
export interface SaleInvoice {
  id: number
  contractRef: string | null
  buyerName: string | null
  buyersRef: string | null
  contractDate: string | null
  contractWeightKg: number | null
  quality: string | null
  deliveryTerms: string | null
  warehouse: string | null
  paymentTerms: string | null
  paymentWeightTerms: string | null
  contractType: string | null
  toBank: string | null
  invoiceNo: string | null
  vatNo: string | null
  lstDoNo: string | null
  customsInvNo: string | null
  customerInvoiceNumber: string | null
  invoiceType: string | null
  invoiceDate: string | null
  noBag: number | null
  packaging: string | null
  goodsDescription: string | null
  othersDescription: string | null
  finalPriceUsdmt: number | null
  fpFx: number | null
  finalPriceVndkg: number | null
  ppClosingPrice: number | null
  ppDiffs: number | null
  ppPriceUsdmt: number | null
  ppFx: number | null
  ppPriceVndkg: number | null
  stoploss: number | null
  customerInvGw: number | null
  customerInvNw: number | null
  customerInvPrice: string | null
  customerInvTotal: number | null
  customerInvAdjust: number | null
  customerInvAtd: string | null
  customInvGw: number | null
  customInvNw: number | null
  customInvPrice: number | null
  customInvTotal: number | null
  receiptWh: string | null
  receiptGw: number | null
  receiptNw: number | null
  blNumber: string | null
  rcEtaAta: string | null
  docsPresentationDate: string | null
  rcTrackingNo: string | null
  rcDocsDeliveredDate: string | null
  rcDueDate: string | null
  rcPaidDate: string | null
  rcPaidAmount: number | null
  blDate: string | null
  commercialInvDateShow: string | null
  rcPaidDetails: string | null
  released: number | null
  toBeReleased: number | null
  toBePaid: number | null
}

export interface SaleInvoiceListResponse {
  items: SaleInvoice[]
  total: number
  page: number
  pageSize: number
}

export interface SaleInvoiceListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}


export interface InvLookupItem { value: string; label: string }
export interface InvLookupResponse { items: InvLookupItem[]; total: number }
export interface InvDoWeightRow {
  doNo: string
  customerNw: number
  customerGw: number
  customsNw: number
  customsGw: number
  receiptNw: number
  receiptGw: number
  numberBags: number
  doDate: string | null
}
export interface InvDoWeightResponse { items: InvDoWeightRow[] }

/** GET /{id}/form shape (base-table codes) for Edit/Copy hydrate. */
export type SaleInvoiceForm = Record<string, unknown> & { id: number }

/** contract_ref_AfterUpdate derived readonly fields. */
export interface InvContractSnapshot {
  contractFxAuto: number
  contractPriceUnit: string
  contractType: string
  contractPrice: number
  finalPriceUsd: number
  contractGrade: string
  goodsDescription: string
}

/** Create/Update payload — frmCtrlSalesQryInvoiceAdd → invoice_scs (camelCase). */
export interface SaleInvoiceWrite {
  contractRef?: string | null
  toBank?: string | null
  invoiceNo?: string | null
  vatNo?: string | null
  customsInvNo?: string | null
  customerInvoiceNumber?: string | null
  invoiceDate?: string | null
  blDate?: string | null
  commercialInvDate?: string | null
  goodsDescription?: string | null
  othersDescription?: string | null
  fpFx?: number | null
  ppClosingPrice?: number | null
  ppFx?: number | null
  customerInvAdjust?: number | null
  receiptWh?: string | null
  customersCreditNo?: string | null
  reclaim?: string | null
  paidDetails?: string | null
  // Readonly-display fields Access still persists — carried through (hydrated on
  // Edit, empty on New/Copy) so a save does not blank them.
  customerInvGw?: number | null
  customerInvNw?: number | null
  customInvGw?: number | null
  customInvNw?: number | null
  receiptGw?: number | null
  receiptNw?: number | null
  toBeReleased?: number | null
  toBePaid?: number | null
  // "For DO" subform — full set of delivery orders attached to this invoice.
  doNos?: string[]
}

export interface SaleInvoiceReceivablesWrite {
  rcEtaAta?: string | null
  rcTrackingNo?: string | null
  rcDocsDeliveredDate?: string | null
  rcDueDate?: string | null
  rcPaidDate?: string | null
  rcPaidAmount?: number | null
  rcPaidDetails?: string | null
}

export interface SaleInvoiceAddDoRequest {
  customsInvNo: string
  invoiceDate?: string | null
  doNo: string
}

export const saleInvoiceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleInvoices: builder.query<SaleInvoiceListResponse, SaleInvoiceListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-invoices',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleInvoice'],
    }),

    getSaleInvoice: builder.query<SaleInvoice, number>({
      query: (id) => `/sale-invoices/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleInvoice', id }],
    }),

    deleteSaleInvoice: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-invoices/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleInvoice'],
    }),

    /** Email-permissioned hard-delete access (legacy btnDelete rule). */
    getSaleInvoiceCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-invoices/access/can-delete',
    }),

    // ── frmCtrlSalesQryInvoiceAdd suggest/check (Suggest_IndexName invoice_scs) ──
    suggestInvoiceNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-invoices/suggest/invoice-no',
    }),
    checkInvoiceNo: builder.query<SuggestIndexResponse, { index: number; excludeId?: number }>({
      query: ({ index, excludeId }) => ({
        url: '/sale-invoices/check/invoice-no',
        params: { index, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // Duplicate check trên customs_inv_no đầy đủ (INV.../VAT...) — cảnh báo sớm khi
    // blur No.Inv / No.VAT. Không được sài lại số cũ ở New/Copy.
    checkInvoiceCustomsNo: builder.query<{ customsInvNo: string; exists: boolean }, { customsInvNo: string; excludeId?: number }>({
      query: ({ customsInvNo, excludeId }) => ({
        url: '/sale-invoices/check/customs-no',
        params: { customsInvNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),

    // ── frmCtrlSalesQryInvoiceAdd create / update / form / snapshot ──────────
    getSaleInvoiceForm: builder.query<SaleInvoiceForm, number>({
      query: (id) => `/sale-invoices/${id}/form`,
    }),
    createSaleInvoice: builder.mutation<{ invoice_scs_id: number }, SaleInvoiceWrite>({
      query: (body) => ({ url: '/sale-invoices', method: 'POST', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    updateSaleInvoice: builder.mutation<{ invoice_scs_id: number }, { id: number; body: SaleInvoiceWrite }>({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    getInvContractSnapshot: builder.query<InvContractSnapshot, string>({
      query: (contractRef) => ({ url: '/sale-invoices/lookups/contract-snapshot', params: { contractRef } }),
      keepUnusedDataFor: 0,
    }),

    // ── Receivables Check + Add DO (shared endpoints for FE-only sub-agents) ──
    updateSaleInvoiceReceivables: builder.mutation<
      { invoice_scs_id: number },
      { id: number; body: SaleInvoiceReceivablesWrite }
    >({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}/receivables`, method: 'PUT', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    addSaleInvoiceDo: builder.mutation<
      { invoice_no: string; delivery_order: string },
      { id: number; body: SaleInvoiceAddDoRequest }
    >({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}/add-do`, method: 'POST', body }),
      invalidatesTags: ['SaleInvoice'],
    }),

    // ── Lookups (combo sources) ──────────────────────────────────────────────
    invLookupContracts: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/contracts', params: q ? { q } : {} }),
    }),
    invLookupBanks: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/banks', params: q ? { q } : {} }),
    }),
    invLookupDeliveryOrders: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/delivery-orders', params: q ? { q } : {} }),
    }),
    // Per-DO weight breakdown (NW/GW Customer/Customs/Receipt + bags) cho bảng
    // "For DO" đã chọn. Nguồn = v_invoice_lst_do_all (GROUP BY do_no + SUM như
    // Access do_no_AfterUpdate). Arg = danh sách do_no đã chọn.
    invDoWeights: builder.query<InvDoWeightResponse, string[]>({
      query: (doNos) => ({ url: '/sale-invoices/lookups/do-weights', params: { doNos: doNos.join(',') } }),
    }),
  }),
})

export const {
  useGetSaleInvoicesQuery,
  useGetSaleInvoiceQuery,
  useDeleteSaleInvoiceMutation,
  useGetSaleInvoiceCanHardDeleteQuery,
  useLazySuggestInvoiceNoQuery,
  useLazyCheckInvoiceNoQuery,
  useLazyCheckInvoiceCustomsNoQuery,
  useLazyGetSaleInvoiceFormQuery,
  useCreateSaleInvoiceMutation,
  useUpdateSaleInvoiceMutation,
  useLazyGetInvContractSnapshotQuery,
  useUpdateSaleInvoiceReceivablesMutation,
  useAddSaleInvoiceDoMutation,
  useInvLookupContractsQuery,
  useInvLookupBanksQuery,
  useInvLookupDeliveryOrdersQuery,
  useInvDoWeightsQuery,
} = saleInvoiceApi


// ===================== saleControlInvoicePrintApi =====================
/**
 * Sales Control › Invoice PRINT preview queries (Access Invoice-screen toolbar).
 * New slice — conflict-free with saleInvoiceApi.ts (owned by another agent).
 *
 *   getInvoiceCustomsPrintPreview  → GET /sale-invoices/print/customs/preview
 *   getInvoiceCustomerPrintPreview → GET /sale-invoices/print/customer/preview
 *   getInvoicePklPrintPreview      → GET /sale-invoices/print/pkl/preview
 *
 * Each returns { title, html, message }. 422 detail = user-facing English
 * message (invoice not found / no rows). keepUnusedDataFor: 0 (always refetch).
 */

/** PKL chooser cbPkl_type combo → mode param. */
export type PklMode = 'OnSpot' | 'OnSpotDone' | 'Direct'

export const saleInvoicePrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Print — Customs: rpCtrlSalesInvCustoms (filter customs_inv_no).
    getInvoiceCustomsPrintPreview: builder.query<PrintPreview, string>({
      query: (customsInvNo) => ({
        url: '/sale-invoices/print/customs/preview',
        params: { customsInvNo },
      }),
      keepUnusedDataFor: 0,
    }),
    // Print — Customer: rpCtrlSalesInvCustomer (+ bonded/KNQ variant).
    getInvoiceCustomerPrintPreview: builder.query<PrintPreview, string>({
      query: (customerInvoiceNumber) => ({
        url: '/sale-invoices/print/customer/preview',
        params: { customerInvoiceNumber },
      }),
      keepUnusedDataFor: 0,
    }),
    // PKL: chooser mode (cbPkl_type) selects the report family/variant.
    getInvoicePklPrintPreview: builder.query<
      PrintPreview,
      {
        customerInvoiceNumber: string; mode: PklMode
        typePrint?: string; cropYear?: string; cargo?: string; customsCustomer?: string
      }
    >({
      query: ({ customerInvoiceNumber, mode, typePrint, cropYear, cargo, customsCustomer }) => ({
        url: '/sale-invoices/print/pkl/preview',
        params: { customerInvoiceNumber, mode, typePrint, cropYear, cargo, customsCustomer },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
  overrideExisting: false,
})

export const {
  useLazyGetInvoiceCustomsPrintPreviewQuery,
  useLazyGetInvoiceCustomerPrintPreviewQuery,
  useLazyGetInvoicePklPrintPreviewQuery,
} = saleInvoicePrintApi


// ===================== saleControlMovingApi =====================
/** Mirrors FastAPI `SaleMovingOut` (camelCase aliases) — frmCtrlSalesQryMoving datasheet. */
export interface SaleMoving {
  id: number
  moveNo: string | null
  moveDate: string | null
  warehouseType: string | null
  fromWarehouse: string | null
  loadingDate: string | null
  netWeightKg: number | null
  toWarehouse: string | null
  quality: string | null
  certification: string | null
  packageCode: string | null
  packaging: string | null
  noContTruck: string | null
  truckCompany: string | null
  truckName: string | null
  pic: string | null
  fullName: string | null
  note: string | null
  buyersRef: string | null
  phone: string | null
  picPhone: string | null
  netWeightKgPp: number | null
  noContTruckInt: number | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface SaleMovingListResponse {
  items: SaleMoving[]
  total: number
  page: number
  pageSize: number
}

export interface SaleMovingListQuery {
  page: number
  pageSize: number
}

/** frmCtrlSalesQryMovingAdd New/Edit payload — camelCase → moving_order_scs. */
export interface SaleMovingWrite {
  moveNo?: string | null
  moveDate?: string | null
  loadingDate?: string | null
  netWeightKg?: number | null
  fromWarehouse?: string | null
  toWarehouse?: string | null
  quality?: string | null
  certification?: string | null
  packageCode?: string | null
  noContTruck?: string | null
  truckCompany?: string | null
  pic?: string | null
  note?: string | null
}

export type SaleMovingForm = Record<string, unknown> & { id: number }

export interface MovingLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface MovingLookupResponse { items: MovingLookupItem[]; total: number }
export interface SuggestMoveNoResponse { index: number; moveNo: string; exists: boolean }

export const saleMovingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleMovings: builder.query<SaleMovingListResponse, SaleMovingListQuery>({
      query: ({ page, pageSize }) => ({ url: '/sale-movings', params: { page, pageSize } }),
      providesTags: ['SaleMoving'],
    }),

    getSaleMoving: builder.query<SaleMoving, number>({
      query: (id) => `/sale-movings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleMoving', id }],
    }),

    deleteSaleMoving: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-movings/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleMoving'],
    }),

    bulkDeleteSaleMoving: builder.mutation<{ deleted: number }, number[]>({
      query: (ids) => ({ url: '/sale-movings/bulk-delete', method: 'POST', body: { ids } }),
      invalidatesTags: ['SaleMoving'],
    }),

    getSaleMovingCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-movings/access/can-delete',
    }),

    // ── frmCtrlSalesQryMovingAdd ──────────────────────────────────────────
    getSaleMovingForm: builder.query<SaleMovingForm, number>({
      query: (id) => `/sale-movings/${id}/form`,
    }),
    createSaleMoving: builder.mutation<{ moving_order_scs_id: number }, SaleMovingWrite>({
      query: (body) => ({ url: '/sale-movings', method: 'POST', body }),
      invalidatesTags: ['SaleMoving'],
    }),
    updateSaleMoving: builder.mutation<{ moving_order_scs_id: number }, { id: number; body: SaleMovingWrite }>({
      query: ({ id, body }) => ({ url: `/sale-movings/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleMoving'],
    }),
    suggestMoveNo: builder.query<SuggestMoveNoResponse, void>({
      query: () => '/sale-movings/suggest/move-no',
      keepUnusedDataFor: 0,
    }),
    checkMoveNo: builder.query<SuggestMoveNoResponse, { moveNo: string; excludeId?: number }>({
      query: ({ moveNo, excludeId }) => ({
        url: '/sale-movings/check/move-no',
        params: { moveNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    movingLookupWarehouses: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/warehouses', params: q ? { q } : {} }),
    }),
    movingLookupQualities: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/qualities', params: q ? { q } : {} }),
    }),
    movingLookupCertifications: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/certifications', params: q ? { q } : {} }),
    }),
    movingLookupTruckCompanies: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/truck-companies', params: q ? { q } : {} }),
    }),
    movingLookupPackages: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/packages', params: q ? { q } : {} }),
    }),
    movingLookupPics: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/pics', params: q ? { q } : {} }),
    }),
    // Print — 1..5 selected records (by move_no) → rpCtrlSalesMoving_Many_N (one dialog).
    getMovingPrintPreview: builder.mutation<{ title: string; html: string; message?: string }, string[]>({
      query: (moveNos) => ({ url: '/sale-movings/print/preview', method: 'POST', body: { moveNos } }),
    }),
  }),
})

export const {
  useGetSaleMovingsQuery,
  useGetSaleMovingQuery,
  useDeleteSaleMovingMutation,
  useBulkDeleteSaleMovingMutation,
  useGetSaleMovingCanHardDeleteQuery,
  useLazyGetSaleMovingFormQuery,
  useCreateSaleMovingMutation,
  useUpdateSaleMovingMutation,
  useLazySuggestMoveNoQuery,
  useLazyCheckMoveNoQuery,
  useMovingLookupWarehousesQuery,
  useMovingLookupQualitiesQuery,
  useMovingLookupCertificationsQuery,
  useMovingLookupTruckCompaniesQuery,
  useMovingLookupPackagesQuery,
  useMovingLookupPicsQuery,
  useGetMovingPrintPreviewMutation,
} = saleMovingApi


// ===================== saleControlReleaseOrderApi =====================
/** Mirrors FastAPI `SaleReleaseOrderOut` (camelCase aliases) — frmCtrlSalesQryRO datasheet. */
export interface SaleReleaseOrder {
  id: number
  customerInvoiceNo: string | null
  releaseNo: string | null
  releaseDate: string | null
  sendToWarehouse: string | null
  containerNo: string | null
  releasableNetWeight: number | null
  releaseNetWeight: number | null
  releaseGrossWeight: number | null
  releaseBag: number | null
  releasableGrossWeight: number | null
  releasableBag: number | null
  whRef: string | null
  contractRef: string | null
  buyersRef: string | null
  deliveryOrder: string | null
  icoNo: string | null
  commodity: string | null
  blNumber: string | null
  blDate: string | null
  loadingPort: string | null
  dischargePort: string | null
  vesselVoyage: string | null
  lstDeliveryTerms: string | null
  whRange: string | null
  whStorageDeadline: string | null
  invoiceQuantity: number | null
  note: string | null
  docsDispatch: string | null
  deliveryFrom: string | null
  isFinalized: number | null
  personUpdate: string | null
  dateUpdate: string | null
}

export interface SaleReleaseOrderListResponse {
  items: SaleReleaseOrder[]
  total: number
  page: number
  pageSize: number
}

export interface SaleReleaseOrderListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryROAdd_upgrade New/Edit payload — camelCase → release_order_scs. */
export interface SaleReleaseOrderDetailWrite {
  invoiceNo?: string | null
  deliveryOrder?: string | null
  containerNo?: string | null
  nwRelease?: number | null
  gwRelease?: number | null
  bagRelease?: number | null
}
export interface SaleReleaseOrderWrite {
  customerInvoiceNo?: string | null
  releaseNo?: string | null
  releaseDate?: string | null
  sendToWarehouse?: string | null
  whStorageDeadline?: string | null
  whRange?: string | null
  note?: string | null
  details?: SaleReleaseOrderDetailWrite[] | null
}

/** GET /{id}/form shape for Edit hydrate. */
export type SaleReleaseOrderForm = Record<string, unknown> & { id: number }

/** frmCtrlSalesQryROAdd_upgrade_detail — Release Details datasheet row. */
export interface SaleReleaseOrderDetailRow {
  id: number
  releaseNo: string | null
  invoiceNo: string | null
  deliveryOrder: string | null
  containerNo: string | null
  nwRelease: number | null
  gwRelease: number | null
  bagRelease: number | null
  package: string | null
  icoNo: string | null
  dateUpdated: string | null
  personUpdated: string | null
}
export interface SaleReleaseOrderDetailListResponse {
  items: SaleReleaseOrderDetailRow[]
  total: number
}

/** Container picker row (v_invoice_lst_container) for the New/Edit dialog. */
export interface ReleasableContainer {
  id: number
  invoice_no: string | null
  delivery_order: string | null
  container_no: string | null
  nw_cont: number | null
  gw_cont: number | null
  bag_cont: number | null
  nw_releasable: number | null
  gw_releasable: number | null
  bag_releasable: number | null
}

export interface RoLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface RoLookupResponse { items: RoLookupItem[]; total: number }
export interface SuggestReleaseNoResponse { releaseNo: string | null; exists: boolean }

export const saleReleaseOrderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleReleaseOrders: builder.query<SaleReleaseOrderListResponse, SaleReleaseOrderListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-release-orders',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleReleaseOrder'],
    }),

    getSaleReleaseOrder: builder.query<SaleReleaseOrder, number>({
      query: (id) => `/sale-release-orders/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleReleaseOrder', id }],
    }),

    deleteSaleReleaseOrder: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-release-orders/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),

    getSaleReleaseOrderCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-release-orders/access/can-delete',
    }),

    // ── frmCtrlSalesQryROAdd_upgrade ──────────────────────────────────────
    getSaleReleaseOrderForm: builder.query<SaleReleaseOrderForm, number>({
      query: (id) => `/sale-release-orders/${id}/form`,
    }),
    createSaleReleaseOrder: builder.mutation<{ release_order_scs_id: number }, SaleReleaseOrderWrite>({
      query: (body) => ({ url: '/sale-release-orders', method: 'POST', body }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),
    updateSaleReleaseOrder: builder.mutation<
      { release_order_scs_id: number },
      { id: number; body: SaleReleaseOrderWrite }
    >({
      query: ({ id, body }) => ({ url: `/sale-release-orders/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),
    suggestReleaseNo: builder.query<SuggestReleaseNoResponse, string>({
      query: (customerInvoiceNo) => ({
        url: '/sale-release-orders/suggest/release-no',
        params: { customerInvoiceNo },
      }),
      keepUnusedDataFor: 0,
    }),
    checkReleaseNo: builder.query<SuggestReleaseNoResponse, { releaseNo: string; excludeId?: number }>({
      query: ({ releaseNo, excludeId }) => ({
        url: '/sale-release-orders/check/release-no',
        params: { releaseNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getReleaseOrderDetails: builder.query<
      SaleReleaseOrderDetailListResponse,
      { releaseNo?: string; shipmentPeriodFrom?: string; shipmentPeriodTo?: string; finalizedFilter?: string }
    >({
      // "Detail (All)": no releaseNo → same tab filters as the list (Shipment Period +
      // Finalized), Access btnDetails global filter. New/Edit form passes releaseNo to
      // scope to one release order.
      query: ({ releaseNo, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-release-orders/details',
        params: {
          ...(releaseNo ? { releaseNo } : {}),
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          finalizedFilter: finalizedFilter ?? 'all',
        },
      }),
      keepUnusedDataFor: 0,
    }),
    getReleasableContainers: builder.query<ReleasableContainer[], string>({
      query: (customerInvoiceNo) => ({
        url: '/sale-release-orders/releasable-containers',
        params: { customerInvoiceNo },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    roLookupCustomerInvoices: builder.query<RoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-release-orders/lookups/customer-invoices', params: q ? { q } : {} }),
    }),
    roLookupSendToWarehouse: builder.query<RoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-release-orders/lookups/send-to-warehouse', params: q ? { q } : {} }),
    }),
    // Print — rpCtrlSalesRO (header + N detail rows) → web HTML preview.
    getReleaseOrderPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (releaseNo) => ({ url: '/sale-release-orders/print/preview', params: { releaseNo } }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetSaleReleaseOrdersQuery,
  useGetSaleReleaseOrderQuery,
  useDeleteSaleReleaseOrderMutation,
  useGetSaleReleaseOrderCanHardDeleteQuery,
  useLazyGetSaleReleaseOrderFormQuery,
  useCreateSaleReleaseOrderMutation,
  useUpdateSaleReleaseOrderMutation,
  useLazySuggestReleaseNoQuery,
  useLazyCheckReleaseNoQuery,
  useGetReleaseOrderDetailsQuery,
  useLazyGetReleaseOrderDetailsQuery,
  useLazyGetReleasableContainersQuery,
  useRoLookupCustomerInvoicesQuery,
  useRoLookupSendToWarehouseQuery,
  useLazyGetReleaseOrderPrintPreviewQuery,
} = saleReleaseOrderApi
